import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, Smartphone } from "lucide-react";
import { toast } from "sonner";

export default function StoreSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);

  const save = async () => { await api.put("/admin/settings", s); toast.success("Settings saved"); };
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setSocial = (k, v) => setS((p) => ({ ...p, social: { ...p.social, [k]: v } }));

  if (!s) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="animate-spin text-gold" size={36} /></div></AdminLayout>;

  const field = "bg-input border-gold/25 text-cream rounded-xl";
  const card = "gold-border-card rounded-xl bg-card-surface p-6";

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl text-cream">Store Settings</h1>
        <Button className="btn-gold rounded-full font-semibold" onClick={save} data-testid="save-settings-btn"><Save size={16} className="mr-1" /> Save</Button>
      </div>

      <div className="space-y-6">
        <div className={card}>
          <h2 className="font-serif text-xl text-gold mb-4 flex items-center gap-2"><Smartphone size={20} /> Cash App Payment</h2>
          <Label className="text-cream/70 text-sm">Business Cash App $Cashtag</Label>
          <Input className={`${field} font-mono`} value={s.cashtag} onChange={(e) => set("cashtag", e.target.value)} data-testid="settings-cashtag" placeholder="$OverflowPopcornCo" />
          <p className="text-cream/40 text-xs mt-2">Customers pay to this Cashtag. Orders stay "Awaiting Payment" until you confirm. Credentials are never stored.</p>
        </div>

        <div className={card}>
          <h2 className="font-serif text-xl text-gold mb-4">Business Info</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-cream/70 text-sm">Business Name</Label><Input className={field} value={s.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="settings-name" /></div>
            <div><Label className="text-cream/70 text-sm">Contact Email</Label><Input className={field} value={s.email} onChange={(e) => set("email", e.target.value)} data-testid="settings-email" /></div>
            <div><Label className="text-cream/70 text-sm">Phone</Label><Input className={field} value={s.phone} onChange={(e) => set("phone", e.target.value)} data-testid="settings-phone" /></div>
            <div><Label className="text-cream/70 text-sm">Tagline</Label><Input className={field} value={s.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="settings-tagline" /></div>
          </div>
        </div>

        <div className={card}>
          <h2 className="font-serif text-xl text-gold mb-4">Pickup</h2>
          <div className="space-y-4">
            <div><Label className="text-cream/70 text-sm">Pickup Address / Note</Label><Input className={field} value={s.pickup_address} onChange={(e) => set("pickup_address", e.target.value)} data-testid="settings-pickup-address" /></div>
            <div><Label className="text-cream/70 text-sm">Pickup Instructions</Label><Textarea className={field} value={s.pickup_instructions} onChange={(e) => set("pickup_instructions", e.target.value)} data-testid="settings-pickup-instructions" /></div>
          </div>
        </div>

        <div className={card}>
          <h2 className="font-serif text-xl text-gold mb-4">Cookie Promotion</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><Label className="text-cream/70 text-sm">Cookies per bundle</Label><Input type="number" className={field} value={s.cookie_pair_size} onChange={(e) => set("cookie_pair_size", Number(e.target.value))} data-testid="settings-cookie-size" /></div>
            <div><Label className="text-cream/70 text-sm">Bundle Price ($)</Label><Input type="number" className={field} value={s.cookie_pair_price} onChange={(e) => set("cookie_pair_price", Number(e.target.value))} data-testid="settings-cookie-price" /></div>
            <div><Label className="text-cream/70 text-sm">Promo Label</Label><Input className={field} value={s.cookie_promo_label} onChange={(e) => set("cookie_promo_label", e.target.value)} data-testid="settings-cookie-label" /></div>
          </div>
        </div>

        <div className={card}>
          <h2 className="font-serif text-xl text-gold mb-4">Social Media</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><Label className="text-cream/70 text-sm">Instagram</Label><Input className={field} value={s.social?.instagram || ""} onChange={(e) => setSocial("instagram", e.target.value)} data-testid="settings-instagram" /></div>
            <div><Label className="text-cream/70 text-sm">Facebook</Label><Input className={field} value={s.social?.facebook || ""} onChange={(e) => setSocial("facebook", e.target.value)} data-testid="settings-facebook" /></div>
            <div><Label className="text-cream/70 text-sm">TikTok</Label><Input className={field} value={s.social?.tiktok || ""} onChange={(e) => setSocial("tiktok", e.target.value)} data-testid="settings-tiktok" /></div>
          </div>
        </div>

        <div className={`${card} border-amber-500/30`}>
          <h2 className="font-serif text-xl text-gold mb-2">Notifications</h2>
          <p className="text-cream/60 text-sm">New orders appear live in your Dashboard and Orders page. Email & SMS notifications can be enabled next — they require connecting an email service (Resend) and Twilio for SMS.</p>
        </div>
      </div>
    </AdminLayout>
  );
}
